import React, { useContext, useEffect, useMemo } from 'react';
import { ConfigProvider, DatePicker } from 'antd';
import type { ThemeConfig } from 'antd';
import type { PickerLocale } from 'antd/es/date-picker/generatePicker';
import enUS from 'antd/es/date-picker/locale/en_US';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/es';
import 'dayjs/locale/de';
import 'dayjs/locale/ru';
import 'dayjs/locale/it';
import 'dayjs/locale/pl';
import 'dayjs/locale/nl';
import 'dayjs/locale/sv';
import 'dayjs/locale/pt';
import { useI18n } from '../i18n/I18nContext';
import './HeaderDateRange.css';

export type DateRange = [Dayjs, Dayjs];

/** Plage « N derniers jours » (bornes incluses, aujourd'hui compris). */
export const lastDays = (days: number): DateRange => [
  dayjs().startOf('day').subtract(days - 1, 'day'),
  dayjs().endOf('day'),
];

interface HeaderDateRangeProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

/* Thème clair + couleur d'accent orange de l'app (le thème antd global est sombre). */
const LIGHT_THEME: ThemeConfig = {
  token: {
    colorPrimary: '#ff9500',
    colorPrimaryHover: '#ffa726',
    colorText: '#1a1a1a',
    colorTextSecondary: 'rgba(26, 26, 26, 0.65)',
    colorTextTertiary: 'rgba(26, 26, 26, 0.45)',
    colorTextQuaternary: 'rgba(26, 26, 26, 0.3)',
    colorTextPlaceholder: 'rgba(26, 26, 26, 0.45)',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBorder: '#e3e2e0',
    colorBorderSecondary: '#ececea',
    colorSplit: '#ececea',
    colorFillTertiary: 'rgba(0, 0, 0, 0.04)',
    colorFillSecondary: 'rgba(0, 0, 0, 0.06)',
    borderRadius: 10,
    fontFamily: '"ZalandoSans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  components: {
    DatePicker: {
      cellActiveWithRangeBg: 'rgba(255, 149, 0, 0.14)',
      cellHoverWithRangeBg: 'rgba(255, 149, 0, 0.22)',
      cellRangeBorderColor: 'rgba(255, 149, 0, 0.5)',
      cellHoverBg: 'rgba(0, 0, 0, 0.05)',
      activeBorderColor: '#1a1a1a',
      hoverBorderColor: '#1a1a1a',
      activeShadow: '0 0 0 3px rgba(26, 26, 26, 0.08)',
    },
    Button: {
      colorPrimary: '#ff9500',
      colorPrimaryHover: '#ffa726',
      colorPrimaryActive: '#f08a00',
      primaryColor: '#ffffff',
    },
  },
};

const DAYJS_LOCALE: Record<string, string> = {
  fr: 'fr', es: 'es', de: 'de', 'de-AT': 'de', ru: 'ru', it: 'it', pl: 'pl', nl: 'nl', sv: 'sv', pt: 'pt',
};

/**
 * Sélecteur de période du header : bouton « 13 sept. 2026 – 13 oct. 2026 » qui ouvre
 * un calendrier double (deux mois) avec raccourcis 7 / 14 / 30 derniers jours et bouton Appliquer.
 */
const HeaderDateRange: React.FC<HeaderDateRangeProps> = ({ value, onChange }) => {
  const { t, locale } = useI18n();
  const { locale: antdLocale } = useContext(ConfigProvider.ConfigContext);

  // Mois / jours traduits dans le calendrier
  useEffect(() => {
    dayjs.locale(DAYJS_LOCALE[locale] || 'en');
  }, [locale]);

  // Locale antd du picker avec le bouton de confirmation renommé « Appliquer »
  const pickerLocale = useMemo<PickerLocale>(() => {
    const base = (antdLocale?.DatePicker as PickerLocale | undefined) ?? enUS;
    return { ...base, lang: { ...base.lang, ok: t.app.dashboard.applyPeriod ?? 'Apply' } };
  }, [antdLocale, t]);

  const presets = useMemo(
    () => [
      { label: t.app.dashboard.last7days, value: lastDays(7) },
      { label: t.app.dashboard.last14days ?? 'Last 14 days', value: lastDays(14) },
      { label: t.app.dashboard.last30days, value: lastDays(30) },
    ],
    [t],
  );

  return (
    <ConfigProvider theme={LIGHT_THEME}>
      <DatePicker.RangePicker
        className="header-date-range"
        popupClassName="header-date-range-popup"
        value={value}
        onChange={(dates) => {
          if (dates && dates[0] && dates[1]) {
            onChange([dates[0].startOf('day'), dates[1].endOf('day')]);
          }
        }}
        allowClear={false}
        needConfirm
        presets={presets}
        format="D MMM YYYY"
        separator={<span className="header-date-range-sep">–</span>}
        prefix={<CalendarOutlined className="header-date-range-icon" />}
        suffixIcon={null}
        maxDate={dayjs().endOf('day')}
        placement="bottomLeft"
        locale={pickerLocale}
      />
    </ConfigProvider>
  );
};

export default HeaderDateRange;
